// src/pages/WalletStatement.jsx — v16
// CHANGED FROM v15 (19 Jul 2026): REAL fix, finally correct — Amount
// and Total were rendering as two lines STACKED in the same vertical
// slot (a flex-column), not as actual separate columns — Amit
// confirmed via screenshot they were reading as one crammed area, not
// two distinct fields. This was the actual gap across every earlier
// "Total column" fix: the crash-safety patch (v15) was correct but
// solved a different problem than the one being asked for. Now Amount
// and Total render as genuinely separate side-by-side columns with
// their own fixed width and a light divider between them, plus small
// column labels so it's unambiguous which is which.
// CHANGED FROM v14 (19 Jul 2026): fixed a real crash risk in the Total
// column — if a backend entry is missing BOTH e.total and
// e.balanceAfter, the old code called .toLocaleString() directly on
// undefined, which throws. That could make the Total (or the whole
// row, depending on where React caught it) silently disappear for any
// entry with incomplete data — this is what Amit was seeing as "the
// Total column is missing". Now falls back to "Bal: —" instead of
// crashing whenever the number isn't actually available, so the
// column always renders something no matter what the data looks like.
// Actual balance-accuracy investigation (why some entries show
// suspicious values) is separate, deferred per Amit's explicit call —
// this fix is scoped to "always show the column," not "get the number
// right."
// CHANGED FROM v13 (19 Jul 2026): fixed the actual bug behind the
// "ledger Total column missing" report — the layout WAS already
// correctly stacking Amount and Total in two rows (confirmed via
// screenshot), but the Total figure had no ₹ symbol and no label, so
// it displayed as a bare, confusing number (e.g. "10") that didn't
// visually read as a running balance. Fixed to show "Bal: ₹10" (with
// thousands separators via toLocaleString) so it's unambiguous, and
// the Pending row now says "Not yet credited" instead of a dash, for
// the same reason. No other layout/structure changes.
// CHANGED FROM v12 (18 Jul 2026): real 2-column ledger layout added —
// Amount and running Total now sit in their own right-hand column,
// stacked vertically, instead of Total being buried as inline text
// under the date. Colors, fonts, tag styling all UNCHANGED from v12
// (Amit's explicit ask — structure changes, formatting doesn't).
// CHANGED FROM v11 (18 Jul 2026): "Total" column now color-coded to
// match the amount — green for Earned, red for Redeemed, amber for
// Expired, plain/neutral for Pending and other types. Layout, fonts,
// spacing all UNCHANGED from what was already live — this is a color
// change only, not a redesign (per Amit: keep it exactly as it looks
// today, just extend the existing color logic to the Total figure too).
// CHANGED FROM v10 (18 Jul 2026): the static, always-said-"30 days"
// 4th tile is gone — replaced with a prominent amber banner above the
// (now 3-tile) summary grid, showing the real soonest-expiring amount
// and a live countdown ("expiring tomorrow" / "in 3 days"), sourced
// from Code.gs v40's new soonestExpiryAmount/soonestExpiryDate fields.
// Auto-hides when nothing is expiring soon (amount is 0). Correctly
// shifts to the NEXT batch once the current soonest one actually
// expires, since it's real FIFO data now, not a static lump sum.
// CHANGED FROM v9 (17 Jul 2026, same night — P51): Cashback Earned tab
// now also shows "pending cashback" rows for Paid orders that haven't
// credited yet (backend now sends these as sourceType 'PendingCashback'
// — see Code.gs v37). Rendered with an amber "Pending" tag instead of
// the tier/wallet-only tag, amount prefixed with ~ (estimate, not
// final), and "Not yet credited" instead of a Total figure since
// balance hasn't actually moved. These rows are excluded from the All
// Wallet Activity tab — they're not real ledger transactions.
// CHANGED FROM v8 (17 Jul): each statement line now shows a small tag —
// "Counts toward tier" (green, sourceType 'Earned' — traces back to a
// clean purchase with no return) or "Wallet only" (gray — return
// credits, redemptions, admin adjustments, wallet restores). This
// makes explicit what Amit and Claude confirmed earlier in the session:
// tier/lifetime spend and wallet balance are two separate concepts,
// and only genuine no-return purchase cashback counts toward tier.
// CHANGED FROM v7 (17 Jul): 4th summary tile label shortened from
// "Expiring this month" to "Expiring soon*" — the longer wording was
// making that tile visually bigger than the other three (Earned/
// Redeemed/Expired), breaking the equal-size tile row. A small
// footnote ("*expiring within 30 days") was added directly below the
// tile row to keep the meaning clear. Underlying data/logic (30-day
// expiringThisMonth figure from the backend) is unchanged — label and
// footnote only.
// CHANGED FROM v1: backend (Code.gs) now returns { summary, lines }
// instead of a bare array, windowed to the last 180 days (matching
// wallet expiry) — updated to consume that shape. Added a summary
// section at the top (Earned / Redeemed / Expired / Expiring This
// Month, all for the last 180 days) and a new "Total" column alongside
// the existing Amount column in each row, per Amit's 13 Jul request.
// CSV download still exports the FULL un-windowed detail via the same
// endpoint shape (Type, Order ID, Product ID, Amount, Balance After).
// CHANGED FROM v2: summary cards laid out in a single row (was 2x2
// grid) using site accent colors as a top-border stripe (green/blue/
// red/gold) matching the rest of the app's color language, instead of
// full pastel backgrounds.
// CHANGED FROM v3: split into two tabs — "Cashback Earned" (purchase-
// based cashback only, excludes return credits/redemptions/admin
// adjustments, uses Code.gs's new sourceType field to correctly scope
// even Expired lines) and "All Wallet Activity" (unchanged, everything).
// Summary cards scope to whichever tab is active — Cashback Earned
// tab shows only Earned + Expired (computed client-side from the
// filtered list), since redemptions draw from one merged FIFO pool
// and can't be cleanly attributed back to cashback-only vs
// return-only origin.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useStore } from '../context/StoreContext';
function csvEscape(val) {
  const s = val === null || val === undefined ? '' : String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}
export default function WalletStatement() {
  const { user, authLoading } = useStore();
  const [statement, setStatement] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [tab, setTab] = useState('cashback'); // 'cashback' | 'all'
  const CASHBACK_ONLY_TYPES = ['Earned', 'Expired', 'Expiring']; // pure cashback lifecycle, excludes returns/redemptions
  const fetchStatement = useCallback(async () => {
    if (!user || !user.customerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/.netlify/functions/get-wallet-statement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: user.customerId }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError('Could not load your cashback statement. Please try again.');
      } else {
        const stmt = data.statement;
        // Backward-compatible: older backend returned a bare array.
        if (Array.isArray(stmt)) {
          setStatement(stmt);
          setSummary(null);
        } else {
          setStatement(stmt?.lines || []);
          setSummary(stmt?.summary || null);
        }
      }
    } catch (err) {
      setError('Network error loading statement.');
    }
    setLoading(false);
  }, [user]);
  useEffect(() => {
    if (authLoading) return;
    fetchStatement();
  }, [authLoading, fetchStatement]);
  function formatDate(iso) {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }
  // NEW (18 Jul 2026): human-friendly countdown for the soonest-expiring
  // wallet batch — "today" / "tomorrow" / "in Xd" — replaces the old
  // static "*Expiring within 30 days" text with a real, live date.
  function formatExpiryCountdown(iso) {
    if (!iso) return '';
    const expiryDate = new Date(iso);
    const now = new Date();
    const daysLeft = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return 'today';
    if (daysLeft === 1) return 'tomorrow';
    return `in ${daysLeft} days`;
  }
  const tabFiltered = useMemo(() => {
    // "All Wallet Activity" excludes PendingCashback rows — those are
    // synthetic estimates, not real ledger transactions, so they don't
    // belong in the full activity view.
    if (tab === 'all') return statement.filter(e => e.sourceType !== 'PendingCashback');
    // "Cashback Earned" tab: real Earned/Expired lines (sourceType
    // Earned) PLUS synthetic PendingCashback rows for orders that
    // haven't credited yet — gives a full picture of cashback in
    // flight, not just what's already landed.
    return statement.filter(e => e.sourceType === 'Earned' || e.sourceType === 'PendingCashback');
  }, [statement, tab]);
  // Since redemptions draw from a single merged FIFO pool (not separate
  // cashback/return sub-balances — that was an intentional earlier
  // design decision), the Cashback Earned tab shows what you've earned
  // and what of that expired, but does NOT attempt to show a running
  // balance or redeemed total for this tab — those concepts don't
  // cleanly separate by source once merged.
  // CHANGED (13 Jul 2026, same day): both tabs now show all 4 cards for
  // visual consistency. Earned/Expired are computed from the CASHBACK-
  // ONLY filtered list (real tab-specific numbers). Redeemed/Expiring
  // This Month can't be cleanly split by source (single merged FIFO
  // pool — see note above), so both tabs show the same site-wide figure
  // for those two cards, sourced from the backend's overall summary.
  const tabSummary = useMemo(() => {
    if (tab === 'cashback') {
      const earned = tabFiltered.filter(e => e.type === 'Earned').reduce((s, e) => s + e.amount, 0);
      const expired = tabFiltered.filter(e => e.type === 'Expired').reduce((s, e) => s + Math.abs(e.amount), 0);
      // CORRECTED (13 Jul 2026): keep 2-decimal precision instead of
      // rounding to a whole rupee — small real cashback totals (e.g.
      // ₹0.35 across several tiny test earnings) were rounding to ₹0
      // and looking like nothing was earned.
      return {
        earned: Math.round(earned * 100) / 100,
        expired: Math.round(expired * 100) / 100,
        redeemed: summary?.redeemed || 0,
        expiringThisMonth: summary?.expiringThisMonth || 0,
      };
    }
    return summary;
  }, [tabFiltered, tab, summary]);
  const filtered = useMemo(() => {
    let list = tabFiltered;
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(e =>
        (e.orderId || '').toLowerCase().includes(s) ||
        (e.productId || '').toLowerCase().includes(s) ||
        (e.description || '').toLowerCase().includes(s)
      );
    }
    if (dateFrom) {
      const from = new Date(dateFrom + 'T00:00:00');
      list = list.filter(e => new Date(e.date) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo + 'T23:59:59');
      list = list.filter(e => new Date(e.date) <= to);
    }
    return list;
  }, [tabFiltered, search, dateFrom, dateTo]);
  function handleDownload() {
    const cols = ['Date', 'Type', 'Description', 'Order ID', 'Product ID', 'Amount', 'Total'];
    const rows = statement.map(e => [
      formatDate(e.date), e.type, e.description, e.orderId, e.productId, e.amount, e.total ?? e.balanceAfter,
    ]);
    const csv = [cols.join(','), ...rows.map(r => r.map(csvEscape).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nb-wallet-statement-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  if (authLoading || loading) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--muted)' }}>Loading your cashback statement…</div>
      </div>
    );
  }
  if (!user) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>Please log in to view your cashback statement.</p>
          <Link to="/login">Log in</Link>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="app-shell">
        <Navbar />
        <div style={{ padding: '2rem 1rem', textAlign: 'center' }}>
          <p>{error}</p>
          <button onClick={fetchStatement}>Retry</button>
        </div>
      </div>
    );
  }
  return (
    <div className="app-shell">
      <Navbar />
      <div style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <h1 style={{ fontSize: 16, fontWeight: 600, color: 'var(--navy)', margin: 0 }}>Cashback Statement</h1>
          {summary && (
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>Last {summary.windowDays} days</span>
          )}
        </div>
        <div className="ws-tabs">
          <button className={`ws-tab ${tab === 'cashback' ? 'active' : ''}`} onClick={() => setTab('cashback')}>Cashback Earned</button>
          <button className={`ws-tab ${tab === 'all' ? 'active' : ''}`} onClick={() => setTab('all')}>All Wallet Activity</button>
        </div>
        {summary && summary.soonestExpiryAmount > 0 && (
          <div className="ws-expiry-banner">
            <i className="ti ti-alert-triangle"></i>
            <div>
              <p className="ws-expiry-amount">₹{summary.soonestExpiryAmount.toLocaleString()} expiring {formatExpiryCountdown(summary.soonestExpiryDate)}</p>
              <p className="ws-expiry-sub">Use it before it's gone — oldest cashback expires first</p>
            </div>
          </div>
        )}
        {tabSummary && (
          <div className="ws-summary-grid">
            <div className="ws-summary-card earned">
              <div className="ws-summary-label">Earned</div>
              <div className="ws-summary-value">+₹{tabSummary.earned.toLocaleString()}</div>
            </div>
            <div className="ws-summary-card redeemed">
              <div className="ws-summary-label">Redeemed</div>
              <div className="ws-summary-value">−₹{tabSummary.redeemed.toLocaleString()}</div>
            </div>
            <div className="ws-summary-card expired">
              <div className="ws-summary-label">Expired</div>
              <div className="ws-summary-value">−₹{tabSummary.expired.toLocaleString()}</div>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <div className="ws-search-box">
            <i className="ti ti-search" style={{ fontSize: 16, color: '#999' }}></i>
            <input
              type="text"
              placeholder="Search order or product ID"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <button
            className="ws-filter-btn"
            onClick={() => setShowDateFilter(v => !v)}
            aria-label="Filter by date"
            style={{ background: showDateFilter ? '#EEF4FF' : '#fff' }}
          >
            <i className="ti ti-calendar"></i>
          </button>
        </div>
        {showDateFilter && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 11, color: '#888' }}>From</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '6px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12 }} />
            <label style={{ fontSize: 11, color: '#888' }}>To</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '6px 8px', border: '0.5px solid rgba(27,42,74,0.2)', borderRadius: 6, fontSize: 12 }} />
            {(dateFrom || dateTo) && (
              <button onClick={() => { setDateFrom(''); setDateTo(''); }} style={{ fontSize: 11, color: '#2F6FED', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
            )}
          </div>
        )}
        <button onClick={handleDownload} className="ws-download-btn">
          <i className="ti ti-download"></i> Download full statement
        </button>
        {filtered.length === 0 ? (
          <p style={{ fontSize: 13, color: '#888', textAlign: 'center', padding: '2rem 0' }}>No cashback activity yet.</p>
        ) : (
          filtered.map((e, idx) => {
            const isPending = e.sourceType === 'PendingCashback';
            // NEW (18 Jul 2026): explicit per-type color, not just sign-based —
            // Earned = green, Redeemed = red, Expired = amber, Pending = no
            // color (plain). Anything else (Return, Admin Credit, Wallet
            // Restored, Manual Override, Admin Deduction) falls back to
            // sign-based coloring (green if positive, red if negative).
            let amountColorClass = 'neutral';
            if (isPending) amountColorClass = 'neutral';
            else if (e.type === 'Earned') amountColorClass = 'earned';
            else if (e.type === 'Redeemed') amountColorClass = 'redeemed';
            else if (e.type === 'Expired') amountColorClass = 'expired';
            else amountColorClass = e.amount >= 0 ? 'earned' : 'redeemed';
            return (
            <div key={idx} className="ws-row ws-row-ledger">
              <div className="ws-row-main">
                <span className="ws-row-desc">{e.description}</span>
                {isPending ? (
                  <span className="ws-row-tag pending">Pending</span>
                ) : (
                  <span className={`ws-row-tag ${e.sourceType === 'Earned' ? 'tier' : 'wallet-only'}`}>
                    {e.sourceType === 'Earned' ? 'Counts toward tier' : 'Wallet only'}
                  </span>
                )}
                <div className="ws-row-bottom-date">{formatDate(e.date)}{e.orderId ? ` · ${e.orderId}` : ''}</div>
              </div>
              <div className="ws-col-amount">
                <div className="ws-col-label">Amount</div>
                <span className={`ws-row-amount ${amountColorClass}`}>
                  {isPending ? '~' : (e.amount >= 0 ? '+' : '')}₹{e.amount}
                </span>
              </div>
              <div className="ws-col-total">
                <div className="ws-col-label">Total</div>
                <span className={`ws-row-total ${amountColorClass}`}>
                  {isPending
                    ? '—'
                    : (typeof (e.total ?? e.balanceAfter) === 'number'
                        ? `₹${(e.total ?? e.balanceAfter).toLocaleString()}`
                        : '—')}
                </span>
              </div>
            </div>
            );
          })
        )}
      </div>
      <style>{`
        .ws-tabs { display: flex; gap: 6px; margin-bottom: 12px; }
        .ws-tab { flex: 1; padding: 9px; border-radius: 6px; border: 0.5px solid rgba(27,42,74,0.2); background: #fff; color: var(--muted); font-size: 12px; font-weight: 500; cursor: pointer; font-family: var(--font); }
        .ws-tab.active { background: var(--navy); border-color: var(--navy); color: #fff; }
        .ws-expiry-banner { display: flex; align-items: center; gap: 12px; background: #FEF8E8; border: 1px solid var(--gold); border-radius: 12px; padding: 14px 16px; margin-bottom: 14px; }
        .ws-expiry-banner i { font-size: 24px; color: #854F0B; flex-shrink: 0; }
        .ws-expiry-amount { font-size: 15px; font-weight: 600; color: #854F0B; margin: 0; }
        .ws-expiry-sub { font-size: 11px; color: #854F0B; margin: 2px 0 0; }
        .ws-summary-grid { display: flex; gap: 6px; margin-bottom: 14px; }
        .ws-summary-card { flex: 1; min-width: 0; border-radius: 8px; overflow: hidden; background: #fff; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
        .ws-summary-label { font-size: 9px; font-weight: 600; padding: 5px 4px; line-height: 1.2; color: #fff; }
        .ws-summary-card.earned .ws-summary-label { background: var(--green); }
        .ws-summary-card.redeemed .ws-summary-label { background: var(--blue); }
        .ws-summary-card.expired .ws-summary-label { background: var(--red); }
        .ws-summary-value { font-size: 13px; font-weight: 700; padding: 8px 4px; background: #fff; }
        .ws-summary-card.earned .ws-summary-value { color: var(--green); }
        .ws-summary-card.redeemed .ws-summary-value { color: var(--blue); }
        .ws-summary-card.expired .ws-summary-value { color: var(--red); }
        .ws-search-box { flex: 1; display: flex; align-items: center; gap: 6px; background: #fff; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; padding: 0 10px; height: 36px; }
        .ws-search-box input { border: none; outline: none; font-size: 13px; flex: 1; background: none; }
        .ws-filter-btn { width: 36px; height: 36px; border: 0.5px solid rgba(27,42,74,0.2); border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #1B2A4A; }
        .ws-download-btn { display: flex; align-items: center; gap: 6px; background: #1B2A4A; border: none; color: #F4F2EE; border-radius: 6px; padding: 8px 14px; font-size: 12px; cursor: pointer; margin-bottom: 14px; width: 100%; justify-content: center; }
        .ws-row { background: #fff; border-radius: 8px; border: 0.5px solid #e5e5e0; padding: 10px 12px; margin-bottom: 8px; }
        /* CHANGED (v16): real 3-part side-by-side layout — Description
           | Amount | Total — Amount and Total are genuinely separate
           columns now, each with their own width and a divider line
           between them, not stacked text in one shared slot. */
        .ws-row-ledger { display: flex; justify-content: space-between; align-items: stretch; gap: 10px; }
        .ws-row-main { flex: 1; min-width: 0; }
        .ws-row-desc { display: block; font-size: 13px; font-weight: 500; color: #1B2A4A; margin-bottom: 4px; }
        .ws-col-amount, .ws-col-total { display: flex; flex-direction: column; align-items: flex-end; justify-content: center; flex-shrink: 0; min-width: 64px; padding-left: 10px; }
        .ws-col-total { border-left: 1px solid rgba(27,42,74,0.1); }
        .ws-col-label { font-size: 8px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
        .ws-row-amount { font-size: 13px; font-weight: 600; white-space: nowrap; }
        .ws-row-amount.earned { color: #1B7A3D; }
        .ws-row-amount.redeemed { color: #D64545; }
        .ws-row-amount.expired { color: #8A5A00; }
        .ws-row-amount.neutral { color: #1B2A4A; }
        .ws-row-total { font-weight: 600; font-size: 13px; white-space: nowrap; }
        .ws-row-total.earned { color: #1B7A3D; }
        .ws-row-total.redeemed { color: #D64545; }
        .ws-row-total.expired { color: #8A5A00; }
        .ws-row-total.neutral { color: #1B2A4A; }
        .ws-row-tag { display: inline-block; font-size: 9px; font-weight: 600; padding: 2px 7px; border-radius: 4px; margin-bottom: 4px; }
        .ws-row-tag.tier { background: #E8F5E9; color: #1B7A3D; }
        .ws-row-tag.wallet-only { background: #F4F2EE; color: var(--muted); }
        .ws-row-tag.pending { background: #FEF8E8; color: #854F0B; }
        .ws-row-bottom-date { font-size: 10px; color: #888; margin-top: 2px; }
      `}</style>
    </div>
  );
}